import { useQuery } from '@tanstack/react-query';
import { Modal } from '@safetyhub/ui';
import { adminApi } from '../../lib/api';
import { RETIRED_TYPE_KEY_LABELS } from '../../constants/retiredTypeKeyLabels';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SCOPE_LABELS: Record<string, string> = {
  system: '시스템',
  organization: '조직',
  site: '현장',
};

export default function HistoryModal({ isOpen, onClose }: HistoryModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'approval-line-meta', 'history'],
    queryFn: () => adminApi.getApprovalLineMetaHistory(),
    enabled: isOpen,
  });

  // 현역 템플릿 key → 이름. `SiteApprovalLineTab`과 같은 queryKey라 탭이 이미 받아 둔 목록을 재사용한다(추가 요청 0).
  // 응답 실패·미도착이면 아래 폴백(은퇴 키 라벨 → raw key)으로 떨어질 뿐 이력 표 자체는 그대로 뜬다.
  const { data: templatesData } = useQuery({
    queryKey: ['admin', 'report-composer', 'templates'],
    queryFn: () => adminApi.listReportTemplates(),
    enabled: isOpen,
  });

  const history = data?.data ?? [];
  const templateNames = new Map((templatesData?.data ?? []).map((t) => [t.key, t.name] as const));

  // 축 값은 템플릿 key. 현역 키는 템플릿 이름으로, 은퇴한 구 type 키의 과거 행만 라벨 맵을 탄다(감사 기록 보존),
  // 어느 쪽에도 없으면(비공개·삭제된 템플릿) raw key.
  const labelOf = (templateKey: string): string =>
    templateNames.get(templateKey) ?? RETIRED_TYPE_KEY_LABELS[templateKey] ?? templateKey;

  return (
    <Modal open={isOpen} onClose={onClose} title="결재라인 메타 변경 이력" size="xl">
      {isLoading ? (
        <div className="text-center py-8 text-gray-400">로딩 중...</div>
      ) : history.length === 0 ? (
        <div className="text-center py-8 text-gray-400">변경 이력이 없습니다.</div>
      ) : (
        <div className="overflow-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">시각</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">양식</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">범위</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">변경자</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">사유</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(h.changedAt).toLocaleString('ko-KR')}
                  </td>
                  <td className="px-3 py-2 text-xs font-medium">{labelOf(h.templateKey)}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="text-gray-700">{SCOPE_LABELS[h.scope] ?? h.scope}</span>
                    {h.scopeId && (
                      <span className="ml-1 text-gray-400 font-mono text-xs">
                        {h.scopeId.slice(0, 8)}…
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 font-mono">
                    {h.changedBy.slice(0, 8)}…
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-xs truncate">
                    {h.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
